import React, { useCallback, useEffect, useMemo, useState } from 'react'
import B2BLayout from '../../../layouts/b2b'
import { Button, Modal, Table, Result, Skeleton, message } from 'antd'
import { PlusOutlined } from '@ant-design/icons'
import fetchAPI, { IHttpError } from '../../../../lib/utils/fetch-api'
import { useNavigate, useLocation } from 'react-router-dom'
import './style.css'
import { DeleteButton, EditButton } from '../../elements/buttons'
import { ResultStatusType } from 'antd/es/result'

interface IError {
  statusCode: ResultStatusType
  message: string
}

/**
 * Компонент списка ролей
 * Отображает таблицу всех ролей с возможностью создания, редактирования и удаления
 */
export const AdminListRoles = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const [data, setData] = useState<any[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [accessError, setAccessError] = useState<IError | null>(null)
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [removableID, setRemovableID] = useState(null)
  const [inRequest, setInRequest] = useState(false)

  useEffect(() => {
    setTimeout(() => {}, 3000)
    getData()
  }, [])

  const getData = useCallback(async () => {
    try {
      const data = await fetchAPI('/api/roles/list', { method: 'GET' })
      if (Array.isArray(data)) {
        setData(data)
        setAccessError(null)
      } else {
        console.error('Неверный тип данных', { data })
        setAccessError({
          statusCode: 'warning',
          message: 'Получены данные неверного формата',
        })
      }
    } catch (err) {
      const statusCode = ((err as IHttpError)?.statusCode || 'error') as ResultStatusType
      const message = (err as IHttpError)?.message || 'Не удалось получить список ролей'
      setAccessError({ message, statusCode })
      console.error('Не удалось получить список', { err })
    } finally {
      setLoading(false)
    }
  }, [])

  /**
   * Хлебные крошки
   */
  const breadcrumbs = useMemo(
    () => [
      {
        href: '/admin',
        title: 'Админ панель',
      },
      {
        href: location.pathname,
        title: 'Список ролей',
      },
    ],
    [location.pathname],
  )

  const handleEditRecord = useCallback(
    id => {
      navigate(`/admin/role/${id}`)
    },
    [navigate],
  )

  const handleNewRecord = useCallback(() => {
    navigate(`/admin/role/new`)
  }, [navigate])

  const handleModalOk = useCallback(async () => {
    setInRequest(true)
    try {
      await fetchAPI(`/api/roles/${removableID}`, { method: 'DELETE' })
      setRemovableID(null)
      setIsModalVisible(false)
      message.success('Роль успешно удалена')
      getData()
    } catch (err) {
      const errorMessage = (err as IHttpError)?.message || 'Не удалось удалить роль'
      message.error(errorMessage)
      console.error('Не удалось удалить роль', { err })
    } finally {
      setInRequest(false)
    }
  }, [removableID, getData])

  const openRemoveModal = useCallback(
    id => {
      setIsModalVisible(true)
      setRemovableID(id)
    },
    [setRemovableID, setIsModalVisible],
  )

  const getRemoveRoleName = useCallback(() => {
    return data.filter(i => i.id === removableID)?.[0]?.['name'] || null
  }, [data, removableID])

  return (
    <B2BLayout breadcrumbs={breadcrumbs}>
      {loading && <Skeleton active />}
      {!loading && accessError && (
        <Result status={accessError.statusCode} title="Ошибка" subTitle={accessError.message} />
      )}
      {!loading && !accessError && (
        <>
          <Modal
            title="Подтвердите действие"
            open={isModalVisible}
            onOk={handleModalOk}
            onCancel={() => {
              setIsModalVisible(false)
            }}
            okText="Удалить"
            cancelButtonProps={{ disabled: inRequest }}
            okButtonProps={{ className: 'ant-btn ant-btn-danger', disabled: inRequest }}
          >
            <h2>Вы уверены, что хотите удалить роль "{getRemoveRoleName()}" ?</h2>
            <p>
              Удаление возможно только при условии, что ни одному из пользователей данная роль не
              назначена. Так же будут очищены все назначенные уровни доступа.
            </p>
          </Modal>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleNewRecord}>
            Создать роль
          </Button>
          <Table
            style={{ marginTop: 20 }}
            rowKey={'id'}
            columns={[
              {
                title: 'ID',
                dataIndex: 'id',
                width: 150,
              },
              {
                title: 'Название',
                dataIndex: 'name',
              },
              {
                title: 'Активно',
                dataIndex: 'is_active',
                render: data => <>{data ? 'Да' : 'Нет'}</>,
                width: 100,
              },
              {
                title: 'Стартовая страница',
                dataIndex: 'start_page',
              },
              {
                title: 'Приоритет',
                dataIndex: 'priority',
              },
              {
                title: 'Действия',
                key: 'operation',
                className: 'no-edit',
                fixed: 'right',
                width: 100,
                align: 'center',
                render: (_, { id }) => (
                  <>
                    <EditButton onClick={() => handleEditRecord(id)} />
                    <DeleteButton onClick={() => openRemoveModal(id)} />
                  </>
                ),
              },
            ]}
            dataSource={data}
            loading={false}
            size="small"
            pagination={false}
          />
        </>
      )}
    </B2BLayout>
  )
}
